import { useEffect, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { SHOP_SHIPPING_NOTICE, centsLabel, type ShopOrder, type ShopOrderDetail, type ShopOrderStatus } from "@gooner/shared";
import { userApi } from "../../lib/api";
import { MASK } from "../../lib/replayMask";
import { useSession } from "../../lib/session";
import { rememberShopOrder, savedShopOrders, shopOrderKeyFor } from "../../lib/shopCart";
import { Button, Field, TextInput } from "../ui/primitives";
import { ShopBackLink } from "../components/ShopBackLink";

const STATUS_LABEL: Record<ShopOrderStatus, string> = {
  paid: "Paid, not shipped yet",
  shipped: "Shipped",
  refunded: "Refunded",
};

const day = (ms: number) => new Date(ms).toLocaleDateString(undefined, { dateStyle: "medium" });

/** Your orders: the account's, plus any this device holds the key to, newest first. */
export function ShopOrders() {
  const user = useSession((s) => s.user);
  const [orders, setOrders] = useState<ShopOrder[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const account = user ? userApi.shopOrders().then((r) => r.orders) : Promise.resolve([] as ShopOrder[]);
    // A saved key that no longer opens anything (a purged or foreign order) is just skipped.
    const device = Promise.all(savedShopOrders().map((o) => userApi.shopOrder(o.id, o.key).catch(() => null)));
    Promise.all([account, device])
      .then(([mine, saved]) => {
        if (cancelled) return;
        const byId = new Map<string, ShopOrder>();
        for (const o of [...mine, ...saved.filter((o): o is ShopOrderDetail => !!o)]) byId.set(o.id, o);
        setOrders([...byId.values()].sort((a, b) => b.createdAt - a.createdAt));
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  return (
    <div className="mx-auto max-w-2xl p-4 sm:p-6">
      <ShopBackLink />
      <h1 className="mt-3 text-2xl font-semibold text-zinc-100">Your orders</h1>
      {failed ? <p className="py-16 text-center text-sm text-zinc-500">Your orders didn't load. Reload the page to try again.</p> : null}
      {orders && orders.length === 0 ? (
        <p className="py-8 text-center text-sm text-zinc-500">
          {user ? "No orders yet." : "No orders on this device yet."}
        </p>
      ) : null}
      {orders?.some((o) => o.status === "paid") ? (
        <p className="mt-2 text-sm text-zinc-400">{SHOP_SHIPPING_NOTICE}</p>
      ) : null}
      <ul className="mt-4 divide-y divide-[var(--color-edge)]">
        {(orders ?? []).map((o) => (
          <li key={o.id}>
            <Link to={`/shop/orders/${o.id}`} className="flex items-baseline justify-between gap-4 py-3 hover:text-zinc-100">
              <span>
                <span className="line-clamp-2 block text-sm font-medium text-zinc-100">{o.productName}</span>
                <span className="block text-xs text-zinc-500">{day(o.createdAt)}, order {o.id}</span>
              </span>
              <span className="shrink-0 text-right text-sm text-zinc-400">{STATUS_LABEL[o.status]}</span>
            </Link>
          </li>
        ))}
      </ul>
      <FindOrder />
    </div>
  );
}

/** A guest's way back with no link: the order number and the email used at checkout. */
function FindOrder() {
  const [number, setNumber] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [found, setFound] = useState<ShopOrderDetail | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      setFound(await userApi.shopFindOrder({ order: number, email }));
    } catch (err) {
      setFound(null);
      setError(err instanceof Error && /^429/.test(err.message) ? "Too many tries. Try again later." : "No order matches that number and email.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="mt-10 border-t border-[var(--color-edge)] pt-6" aria-labelledby="find-order">
      <h2 id="find-order" className="text-lg font-semibold text-zinc-100">Find an order</h2>
      <p className="mt-1 text-sm text-zinc-400">Bought without an account? Use the order number and the email you checked out with.</p>
      <form onSubmit={(e) => void submit(e)} className="mt-3 grid gap-3 sm:grid-cols-[1fr_1.4fr_auto] sm:items-end">
        <Field label="Order number">
          <TextInput required value={number} onChange={(e) => setNumber(e.target.value)} autoComplete="off" />
        </Field>
        <Field label="Email">
          <TextInput required type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Button type="submit" variant="primary" disabled={busy}>
          {busy ? "Looking…" : "Find"}
        </Button>
      </form>
      {error ? (
        <p role="alert" className="mt-2 text-sm text-red-400">
          {error}
        </p>
      ) : null}
      {found ? (
        <div className="mt-6">
          <OrderDetailView order={found} />
        </div>
      ) : null}
    </section>
  );
}

/** One order's status, items, total and address. */
function OrderDetailView({ order }: { order: ShopOrderDetail }) {
  return (
    <>
      <h2 className="text-2xl font-semibold text-zinc-100">
        {order.items.length > 1 ? `${order.items.length} items` : order.productName}
      </h2>
      <p className="mt-1 text-sm text-zinc-500">Order {order.id}, placed {day(order.createdAt)}</p>
      {order.status === "paid" ? <p className="mt-3 text-sm text-zinc-300">{SHOP_SHIPPING_NOTICE}</p> : null}
      <dl className="mt-6 space-y-4 text-sm">
        <div>
          <dt className="text-zinc-500">Status</dt>
          <dd className="text-zinc-100">
            {STATUS_LABEL[order.status]}
            {order.status === "shipped" && order.shippedAt ? ` on ${day(order.shippedAt)}` : ""}
          </dd>
        </div>
        {order.tracking ? (
          <div>
            <dt className="text-zinc-500">Tracking number</dt>
            <dd className="text-zinc-100" {...MASK}>{order.tracking}</dd>
          </div>
        ) : null}
        <div>
          <dt className="text-zinc-500">Items</dt>
          <dd className="text-zinc-100">
            <ul>
              {order.items.map((i) => (
                <li key={i.slug}>
                  {i.quantity} x {i.name}, {centsLabel(i.priceCents)} each
                </li>
              ))}
            </ul>
          </dd>
        </div>
        <div>
          <dt className="text-zinc-500">Paid</dt>
          <dd className="text-zinc-100">{centsLabel(order.priceCents)}</dd>
        </div>
        <div>
          <dt className="text-zinc-500">Ship to</dt>
          {order.shipTo ? (
            <dd className="whitespace-pre-line text-zinc-100" {...MASK}>
              {[
                order.shipTo.name,
                order.shipTo.line1,
                order.shipTo.line2,
                `${order.shipTo.city}, ${order.shipTo.state} ${order.shipTo.zip}`,
              ]
                .filter(Boolean)
                .join("\n")}
            </dd>
          ) : (
            <dd className="text-zinc-400">Deleted after shipping, as promised.</dd>
          )}
        </div>
      </dl>
    </>
  );
}

/** `/shop/orders/:id` — one order, with its address until that is deleted. Opened by the
 *  account that bought it, or by the order's key: from the private link's `#k=`, or saved
 *  on this device. */
export function ShopOrderPage() {
  const { id = "" } = useParams();
  const user = useSession((s) => s.user);
  const [order, setOrder] = useState<ShopOrderDetail | null>(null);
  const [missing, setMissing] = useState(false);
  // Read the link's key once, keep it on this device, and take it out of the address bar so
  // it doesn't end up in a screenshot or a shared tab.
  const [key] = useState(() => {
    const fromLink = /(?:^|[#&])k=([A-Za-z0-9_-]{16,128})/.exec(window.location.hash)?.[1] ?? null;
    if (fromLink) {
      rememberShopOrder(id.toUpperCase(), fromLink);
      window.history.replaceState(window.history.state, "", window.location.pathname + window.location.search);
    }
    return fromLink ?? shopOrderKeyFor(id.toUpperCase());
  });

  useEffect(() => {
    if (!user && !key) {
      setMissing(true);
      return;
    }
    let cancelled = false;
    userApi
      .shopOrder(id, key)
      .then((o) => {
        if (!cancelled) setOrder(o);
      })
      .catch(() => {
        if (!cancelled) setMissing(true);
      });
    return () => {
      cancelled = true;
    };
  }, [id, user?.id, key]);

  return (
    <div className="mx-auto max-w-2xl p-4 sm:p-6">
      <ShopBackLink to="/shop/orders">Your orders</ShopBackLink>
      {missing ? (
        <>
          <p className="py-8 text-center text-sm text-zinc-500">
            {user ? "That order isn't on this account or this device." : "That order isn't saved on this device."}
          </p>
          <FindOrder />
        </>
      ) : null}
      {order ? (
        <div className="mt-3">
          <OrderDetailView order={order} />
        </div>
      ) : null}
    </div>
  );
}
